import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileDown, FileSpreadsheet, Loader2 } from 'lucide-react';
import { getAuthHeader } from '../../services/tokenService';
import Filtres from './Filtres';
import { Camembert, ParTerritoire, Evolution } from './Graphiques';
import Classement from './Classement';
import CarteTerritoires from './CarteTerritoires';
import { filtrer, parActivite, exporterCsv, exporterPdf, eur, aplatir, totaux } from './donnees';

const VIDE = { annees: [], territoires: [], categories: [], recherche: '', mesure: 'contrats' };

const Kpi = ({ label, valeur, testid }) => (
  <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4" data-testid={testid}>
    <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</p>
    <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">{valeur}</p>
  </div>
);

export default function StatsActivitesDashboard() {
  const [data, setData] = useState(null);
  const [f, setF] = useState(VIDE);
  const [pdf, setPdf] = useState(false);
  const rapport = useRef(null);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/stats-activites`, { headers: getAuthHeader() })
      .then((r) => setData(r.data)).catch((e) => toast.error(e.response?.data?.detail || 'Statistiques indisponibles'));
  }, []);

  const base = useMemo(() => aplatir(data?.contrats || []), [data]);
  const options = useMemo(() => {
    const l = base;
    const u = (k) => [...new Set(l.map((x) => x[k]))].sort((a, b) => (typeof a === 'number' ? b - a : String(a).localeCompare(String(b), 'fr')));
    return { annees: u('annee'), territoires: u('territoire'), categories: u('categorie') };
  }, [base]);
  const lignes = useMemo(() => filtrer(base, f), [base, f]);
  const sansTerritoire = useMemo(() => filtrer(base, { ...f, territoires: [] }), [base, f]);
  const classement = useMemo(() => parActivite(lignes, f.mesure), [lignes, f.mesure]);
  const tot = totaux(lignes);

  const pdfClick = async () => { setPdf(true); try { await exporterPdf(rapport.current); } catch { toast.error('Export PDF impossible'); } setPdf(false); };

  if (!data) return <div className="flex items-center gap-2 p-8 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Chargement des statistiques…</div>;
  return (
    <div className="space-y-6" data-testid="stats-activites-dashboard">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Observatoire RCDS</p>
          <h2 className="text-2xl font-semibold text-slate-900">Activités les plus souscrites</h2>
          <p className="mt-1 max-w-2xl text-xs text-slate-500">{data.regle}</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => exporterCsv(classement, f)} className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:border-slate-500" data-testid="stats-export-csv"><FileSpreadsheet className="h-4 w-4" /> CSV</button>
          <button type="button" onClick={pdfClick} disabled={pdf} className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-60" data-testid="stats-export-pdf">{pdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} PDF</button>
        </div>
      </header>
      <Filtres options={options} f={f} setF={setF} />
      <div ref={rapport} className="space-y-6 bg-slate-50/60 p-1">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Kpi label="Contrats retenus" valeur={tot.contrats} testid="stats-kpi-contrats" />
          <Kpi label="Activités distinctes" valeur={classement.length} testid="stats-kpi-activites" />
          <Kpi label="Souscriptions d'activités" valeur={classement.reduce((s, a) => s + a.contrats, 0)} testid="stats-kpi-souscriptions" />
          <Kpi label="Activité n° 1" valeur={classement[0]?.code || '—'} testid="stats-kpi-top" />
        </div>
        {classement.length === 0 ? <p className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500" data-testid="stats-vide">Aucune activité pour ces filtres.</p> : (
          <>
            <CarteTerritoires lignes={sansTerritoire} mesure={f.mesure} selection={f.territoires}
              onToggle={(t) => setF((p) => ({ ...p, territoires: p.territoires.includes(t) ? p.territoires.filter((x) => x !== t) : [...p.territoires, t] }))} />
            <div className="grid gap-6 xl:grid-cols-2">
              <Camembert classement={classement} mesure={f.mesure} />
              <ParTerritoire lignes={lignes} classement={classement} mesure={f.mesure} />
            </div>
            <Evolution lignes={lignes} classement={classement} mesure={f.mesure} />
            <Classement classement={classement} mesure={f.mesure} total={classement.reduce((s, a) => s + a[f.mesure], 0)} />
          </>
        )}
        <p className="text-[11px] text-slate-400">Édité le {new Date().toLocaleString('fr-FR')} · CA total des contrats retenus : {eur(tot.ca)}</p>
      </div>
    </div>
  );
}
